import React from "react";
import { useHistory } from "react-router";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faArrowAltCircleUp, faExclamationTriangle } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { differenceInDays } from "date-fns";
import { graphql } from "relay-runtime";

import { getNextBillingCycleAnchorDate } from "~/billing/utils";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { inCalendarMode, inVideocallMode } from "~/lib/utils";
import { Button, useModals } from "~/ui";
import { IconButton, Modal } from "~/ui/legacyFellowIndex";

import { ReactComponent as CopilotBillingSettings } from "./copilot-billing-settings.svg";

import type { OutOfCreditsModalQuery } from "~/__generated__/OutOfCreditsModalQuery.graphql";

import styles from "./OutOfCreditsModal.module.css";

export function useOpenOutOfCreditsModal() {
	const modals = useModals();

	return React.useCallback(
		() =>
			modals.open(({ close }) => (
				<React.Suspense fallback={null}>
					<OutOfCreditsModal onClose={close} />
				</React.Suspense>
			)),
		[modals],
	);
}

export default function OutOfCreditsModal({ onClose }: { onClose: () => void }) {
	const history = useHistory();

	const { viewer } = useLazyLoadQuery<OutOfCreditsModalQuery>(
		graphql`
			query OutOfCreditsModalQuery {
				viewer {
					hasAdminAccess
					account {
						uri
						billingAccount {
							subscriptionBillingCycleAnchor
							isSubscriptionActive
							subscriptionState
							product {
								legacy
							}
						}
						recordingCredits {
							includedAiMeetingCredits
						}
					}
				}
			}
		`,
		{},
		{ fetchPolicy: "network-only" },
	);

	const { subscriptionBillingCycleAnchor, isSubscriptionActive, subscriptionState, product } =
		viewer.account.billingAccount;
	const { includedAiMeetingCredits } = viewer.account.recordingCredits;

	const nextCycleAnchor = React.useMemo(() => {
		return getNextBillingCycleAnchorDate(subscriptionBillingCycleAnchor);
	}, [subscriptionBillingCycleAnchor]);
	const usageResetsDays = nextCycleAnchor ? differenceInDays(nextCycleAnchor, new Date()) : 0;

	const onAddCreditsClick = React.useCallback(() => {
		inVideocallMode || inCalendarMode
			? window.open(`${viewer.account.uri}/account/billing/overview/#copilot`, "_blank", "noopener")
			: history.push("/account/billing/overview/#copilot");
	}, [history, viewer.account.uri]);

	const onUpgrade = React.useCallback(() => {
		inVideocallMode || inCalendarMode
			? window.open(`${viewer.account.uri}/account/billing/plan`, "_blank", "noopener")
			: history.push("/account/billing/plan");
	}, [history, viewer.account.uri]);

	if (!isSubscriptionActive || subscriptionState === "TRIAL") {
		return (
			<Modal onClose={onClose} width="45.25rem">
				<IconButton icon={faTimes} onClick={onClose} className={styles.closeButton} />
				<Modal.Body className={styles.modalContent}>
					<div className={styles.modalTitle}>
						Upgrade to unlock the <strong>Note Taker</strong>
					</div>
					<div className={styles.modalText}>
						<strong>Bring the power of AI to your meetings.</strong> Record your meetings with the Note Taker to get
						AI-generated summaries, action items, transcripts and so much more!
					</div>
					<CopilotBillingSettings className={styles.modalImage} />
				</Modal.Body>
				<Modal.Footer>
					<Button variant="billing" size="normal" onClick={onUpgrade} data-test-id="ooc-modal-upgrade">
						<FontAwesomeIcon icon={faArrowAltCircleUp} className={styles.upgradeIcon} />
						Upgrade
					</Button>
				</Modal.Footer>
			</Modal>
		);
	}

	if (viewer.hasAdminAccess) {
		return (
			<Modal onClose={onClose} width="45.25rem">
				<IconButton icon={faTimes} onClick={onClose} className={styles.closeButton} />
				<Modal.Body className={styles.modalContent}>
					<div className={styles.modalTitle}>
						Add more <strong>Note Taker credits</strong>
					</div>
					{includedAiMeetingCredits > 0 && (
						<div className={styles.modalWarning}>
							<FontAwesomeIcon icon={faExclamationTriangle} />
							Your workspace has used all available recording credits for the month.{" "}
							{nextCycleAnchor && !product.legacy && (
								<>
									Recording credits will refresh in {usageResetsDays} day{usageResetsDays > 0 && "s"}.
								</>
							)}
						</div>
					)}
					<div className={styles.modalText}>
						To add more credits, you can{" "}
						<strong>adjust your Pay-as-you-go budget or upgrade to Unlimited recordings</strong> in Workspace settings.
					</div>
					<CopilotBillingSettings className={styles.modalImage} />
				</Modal.Body>
				<Modal.Footer>
					<Button variant="primary" size="normal" onClick={onAddCreditsClick} data-test-id="ooc-modal-add-credits">
						Add credits
					</Button>
				</Modal.Footer>
			</Modal>
		);
	}

	return (
		<Modal onClose={onClose} width="45.25rem">
			<IconButton icon={faTimes} onClick={onClose} className={styles.closeButton} />
			<Modal.Body className={styles.modalContent}>
				<div className={styles.modalTitle}>
					Your workspace is out of <strong>Note Taker credits</strong>
				</div>
				{includedAiMeetingCredits > 0 && (
					<div className={styles.modalWarning}>
						<FontAwesomeIcon icon={faExclamationTriangle} />
						Your workspace has used all available recording credits for the month.{" "}
						{nextCycleAnchor && !product.legacy && (
							<>
								Recording credits will refresh in {usageResetsDays} day{usageResetsDays > 0 && "s"}.
							</>
						)}
					</div>
				)}
				<div className={styles.modalText}>
					{/* eslint-disable-next-line max-len */}
					Your workspace admins can <strong>
						adjust your Pay-as-you-go budget or upgrade to Unlimited recordings
					</strong>{" "}
					in Workspace settings.
				</div>
				<CopilotBillingSettings className={styles.modalImage} />
			</Modal.Body>
			<Modal.Footer>
				<Button variant="primary" size="normal" onClick={onClose} data-test-id="ooc-modal-dismiss">
					Got it
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
